import React, { useEffect, useState, useCallback, useMemo } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import SeriesWizard, { validityLabel } from "./series/SeriesWizard";

// A "series" is a pre-paid bundle of ONE service (or ONE package) that a
// customer buys upfront and we auto-deduct as they visit. Created through the
// 4-step wizard (details → item → branches → pricing).

export default function BrandSeries() {
  const { staff } = useAuth();
  const canCreate = hasPermission(staff, "brand.series.create");
  const canEdit = hasPermission(staff, "brand.series.edit");
  const canDelete = hasPermission(staff, "brand.series.delete");
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Compact filter + pagination state (mirrors Brand → Services).
  const [nameFilter, setNameFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = parseInt(localStorage.getItem("brand-series-rpp") || "25", 10);
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });
  useEffect(() => { localStorage.setItem("brand-series-rpp", String(rowsPerPage)); }, [rowsPerPage]);
  useEffect(() => { setPage(1); }, [nameFilter, serviceFilter, statusFilter, rowsPerPage]);

  const load = useCallback(async () => {
    try {
      const [t, s, c, p, b] = await Promise.all([
        staffApi.get("/series-templates"),
        staffApi.get("/services", { params: { active_only: false } }),
        staffApi.get("/categories").catch(() => ({ data: [] })),
        staffApi.get("/packages").catch(() => ({ data: [] })),
        staffApi.get("/branches").catch(() => ({ data: [] })),
      ]);
      setItems(t.data);
      setServices(s.data);
      setCategories(c.data || []);
      setPackages(p.data || []);
      setBranches(b.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const svcMap = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);
  const pkgMap = useMemo(() => Object.fromEntries(packages.map((p) => [p.id, p])), [packages]);
  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);
  const itemLabel = (t) => (t.package_id ? pkgMap[t.package_id]?.name : svcMap[t.service_id]?.name) || "—";
  const branchLabel = (t) => {
    const ids = (t.branch_ids || []).filter((id) => branchMap[id]);
    if (!ids.length) return "All branches";
    if (ids.length === branches.length) return "All branches";
    if (ids.length <= 2) return ids.map((id) => branchMap[id].name).join(", ");
    return `${ids.length} branches`;
  };

  const filteredRows = useMemo(() => {
    const n = nameFilter.trim().toLowerCase();
    return items.filter((t) => {
      if (n && !(t.name || "").toLowerCase().includes(n)) return false;
      if (serviceFilter !== "all" && t.service_id !== serviceFilter && t.package_id !== serviceFilter) return false;
      if (statusFilter === "active" && !t.active) return false;
      if (statusFilter === "inactive" && t.active) return false;
      return true;
    });
  }, [items, nameFilter, serviceFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const clampedPage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((clampedPage - 1) * rowsPerPage, clampedPage * rowsPerPage),
    [filteredRows, clampedPage, rowsPerPage]
  );
  const resetFilters = () => { setNameFilter(""); setServiceFilter("all"); setStatusFilter("all"); };

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setOpen(true); };

  const clone = async (t) => {
    try { await staffApi.post(`/series-templates/${t.id}/clone`); toast.success("Series cloned"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await staffApi.delete(`/series-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <StaffShell context="brand">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="tracking-overline text-xs text-muted-foreground">BRAND CATALOG</p>
          <h1 className="text-3xl font-semibold mt-1" data-testid="series-title">Series</h1>
          <p className="text-sm text-muted-foreground">Pre-paid multi-session bundles. Sold to clients and auto-deducted per visit.</p>
        </div>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-series-btn" className="gap-1"><Plus className="w-4 h-4" /> New series</Button>
        )}
      </div>

      <Card className="rounded-lg mb-3">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Search series name…"
                className="pl-8 h-8 text-sm"
                data-testid="series-name-filter"
              />
            </div>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="h-8 text-xs w-[180px]" data-testid="series-service-filter"><SelectValue placeholder="Service" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                {services.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="series-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {(nameFilter || serviceFilter !== "all" || statusFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 gap-1 text-xs" data-testid="series-filter-reset">
                <X className="w-3.5 h-3.5" /> Reset
              </Button>
            )}
            <div className="ml-auto text-xs text-muted-foreground" data-testid="series-result-count">
              Showing <span className="font-medium text-foreground">{filteredRows.length === 0 ? 0 : (clampedPage - 1) * rowsPerPage + 1}
              –{Math.min(clampedPage * rowsPerPage, filteredRows.length)}</span> of <span className="font-medium text-foreground">{filteredRows.length}</span>
              {filteredRows.length !== items.length && <> · <span className="text-muted-foreground">{items.length} total</span></>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-lg">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Service / Package</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Qty</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Sell price</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Per session</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Validity</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Branches</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-xs text-muted-foreground py-8" data-testid="series-empty-row">
                    {items.length === 0 ? "No series yet — bundle a service into a 3, 5, or 10-visit series." : "No series match your filters."}
                  </TableCell>
                </TableRow>
              )}
              {pagedRows.map((t) => (
                <TableRow key={t.id} className="h-9 hover:bg-muted/40" data-testid={`series-row-${t.id}`}>
                  <TableCell className="font-medium py-1.5 text-sm">
                    <span className="truncate block">{t.name}</span>
                  </TableCell>
                  <TableCell className="py-1.5 text-xs text-muted-foreground">
                    {itemLabel(t)}
                    {t.package_id && <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[9px] rounded-full">Package</Badge>}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs whitespace-nowrap">×{t.quantity}</TableCell>
                  <TableCell className="py-1.5 text-xs whitespace-nowrap">{formatIDR(t.price)}</TableCell>
                  <TableCell className="py-1.5 text-xs whitespace-nowrap" data-testid={`series-per-session-${t.id}`}>
                    {formatIDR(Math.round((t.price || 0) / Math.max(1, t.quantity || 1)))}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs whitespace-nowrap" data-testid={`series-validity-${t.id}`}>{validityLabel(t)}</TableCell>
                  <TableCell className="py-1.5 text-xs whitespace-nowrap" data-testid={`series-branches-${t.id}`}>{branchLabel(t)}</TableCell>
                  <TableCell className="py-1.5">
                    {t.active
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-full border border-emerald-200 h-5 px-2 text-[10px]">Active</Badge>
                      : <Badge variant="secondary" className="rounded-full h-5 px-2 text-[10px]">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="py-1 text-right">
                    <div className="inline-flex items-center gap-0">
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(t)} className="h-7 w-7" data-testid={`edit-series-${t.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>}
                      {canCreate && <Button size="icon" variant="ghost" onClick={() => clone(t)} className="h-7 w-7" data-testid={`clone-series-${t.id}`} title="Duplicate"><Copy className="w-3.5 h-3.5" /></Button>}
                      {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(t)} className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`delete-series-${t.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>}
                      {!canEdit && !canCreate && !canDelete && <span className="text-muted-foreground italic text-xs pr-2">Read-only</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredRows.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs" data-testid="series-pagination">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Rows per page</span>
                <Select value={String(rowsPerPage)} onValueChange={(v) => setRowsPerPage(parseInt(v, 10))}>
                  <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="series-rows-per-page"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Page {clampedPage} of {totalPages}</span>
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={clampedPage <= 1} data-testid="series-page-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={clampedPage >= totalPages} data-testid="series-page-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <SeriesWizard
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        services={services}
        categories={categories}
        packages={packages}
        branches={branches}
        onSaved={load}
      />
    </StaffShell>
  );
}
